/**
 * Diagnosis Selector Component
 */
import React, { useMemo } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Search, X } from 'lucide-react';

export const DiagnosisSelector = ({
  diagnoses,
  selectedDiagnoses,
  onDiagnosisChange,
  diagnosisSearch,
  setDiagnosisSearch
}) => {
  // Sort and filter diagnoses
  const sortedAndFilteredDiagnoses = useMemo(() => {
    let sorted = [...diagnoses].sort((a, b) => {
      const codeA = parseInt(a.code) || 0;
      const codeB = parseInt(b.code) || 0;
      return codeA - codeB;
    });
    
    if (diagnosisSearch.trim()) {
      const search = diagnosisSearch.toLowerCase().trim();
      sorted = sorted.filter(d => 
        d.name.toLowerCase().includes(search) ||
        d.code.toLowerCase().includes(search)
      );
    }
    
    return sorted;
  }, [diagnoses, diagnosisSearch]);

  const getDiagnosisName = (diagId) => {
    const diagnosis = diagnoses.find(d => d.id === diagId);
    if (diagnosis) {
      return `${diagnosis.code}: ${diagnosis.name}`;
    }
    return diagId;
  };

  const addDiagnosis = (diagnosis) => {
    if (!selectedDiagnoses.includes(diagnosis.id)) {
      onDiagnosisChange([...selectedDiagnoses, diagnosis.id]);
    }
    setDiagnosisSearch('');
  };

  const removeDiagnosis = (diagId) => {
    onDiagnosisChange(selectedDiagnoses.filter(d => d !== diagId));
  };

  return (
    <div className="space-y-2">
      <Label>Diagnostice</Label>
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Căutați diagnostic după cod sau denumire..."
            value={diagnosisSearch}
            onChange={(e) => setDiagnosisSearch(e.target.value)}
            className="pl-10 pr-20"
            data-testid="diagnosis-search"
          />
          {diagnosisSearch && (
            <button
              type="button"
              onClick={() => setDiagnosisSearch('')}
              className="absolute right-12 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
            {sortedAndFilteredDiagnoses.length}
          </span>
        </div>
        
        {/* Dropdown list */}
        {diagnosisSearch.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
            {sortedAndFilteredDiagnoses.length === 0 ? (
              <div className="p-3 text-sm text-slate-500 text-center">
                Niciun diagnostic găsit
              </div>
            ) : (
              sortedAndFilteredDiagnoses.slice(0, 50).map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => addDiagnosis(d)}
                  className={`w-full text-left px-3 py-2 hover:bg-emerald-50 border-b border-slate-100 last:border-0 text-sm transition-colors ${
                    selectedDiagnoses.includes(d.id) ? 'bg-slate-50 text-slate-400' : ''
                  }`}
                  data-testid={`diagnosis-option-${d.id}`}
                >
                  <span className="font-medium text-emerald-600">{d.code}</span>
                  <span className="mx-2">-</span>
                  <span>{d.name}</span>
                </button>
              ))
            )}
          </div>
        )}
      </div>
      
      {/* Selected diagnoses */}
      <div className="flex flex-wrap gap-2 mt-2">
        {selectedDiagnoses.map((diagId) => (
          <Badge 
            key={diagId} 
            variant="secondary"
            className="flex items-center gap-1"
          >
            {getDiagnosisName(diagId)}
            <X 
              className="w-3 h-3 cursor-pointer" 
              onClick={() => removeDiagnosis(diagId)}
              data-testid={`remove-diagnosis-${diagId}`}
            />
          </Badge>
        ))}
      </div>
    </div>
  );
};
